import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeSlug from "rehype-slug";
import rehypeAutolink from "rehype-autolink-headings";
import { Clock, ArrowLeft, Tag } from "lucide-react";
import { BLOGS } from "../data/blogs";
import { BLOG_BODIES } from "../content/blog-registry";
import { mdComponents } from "../components/article/mdComponents";
import TableOfContents from "../components/article/TableOfContents";
import ArticleSidebar from "../components/article/ArticleSidebar";
import Citations from "../components/article/Citations";

export default function BlogDetail() {
  const { slug } = useParams();
  const blog = BLOGS.find((b) => b.slug === slug);
  if (!blog) return <Navigate to="/blogs" replace />;
  const body = (BLOG_BODIES[slug] || "").trim();
  const hasBody = body.length > 0 && body !== "# Placeholder";

  return (
    <div data-testid="blog-detail" className="bg-[#F5F5F0] min-h-screen pt-24 pb-20">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="mb-8">
          <Link
            to="/blogs"
            className="font-arcade text-base text-[#15803D] hover:underline uppercase inline-flex items-center gap-2"
          >
            <ArrowLeft size={14} /> Back to dispatches
          </Link>
        </div>

        <div
          className="grid gap-10
                     grid-cols-1
                     lg:grid-cols-[minmax(0,1fr)_240px]
                     xl:grid-cols-[220px_minmax(0,1fr)_240px]"
        >
          <aside className="hidden xl:block">
            <ArticleSidebar
              items={BLOGS}
              currentSlug={slug}
              basePath="/blogs"
              label="DISPATCHES"
            />
          </aside>

          <article className="min-w-0 max-w-[760px] mx-auto w-full">
            <header className="border-b-2 border-[#D4D4D4] pb-8 mb-10">
              <p className="font-arcade text-base text-[#15803D] uppercase tracking-wide mb-3">
                &gt; {blog.category}
              </p>
              <h1 className="font-pixel text-2xl md:text-4xl text-[#0A0A0A] leading-tight mb-5">
                {blog.title}
              </h1>
              <p className="font-terminal text-sm text-[#0A0A0A]/65 leading-relaxed mb-5">
                {blog.excerpt}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-[#0A0A0A]/55">
                <span className="font-terminal text-xs flex items-center gap-1.5">
                  <Clock size={12} /> {blog.read_time} min read
                </span>
                <span className="font-terminal text-xs">
                  {new Date(blog.published_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
              {blog.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-5">
                  {blog.tags.map((t) => (
                    <span key={t} className="tag-chip text-sm flex items-center gap-1">
                      <Tag size={9} /> {t}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {hasBody ? (
              <>
                <div className="prose-melon" data-testid="article-prose">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[
                      rehypeSlug,
                      [rehypeAutolink, { behavior: "wrap" }],
                      rehypeKatex,
                    ]}
                    components={mdComponents}
                  >
                    {body}
                  </ReactMarkdown>
                </div>
                {blog.references?.length > 0 && <Citations refs={blog.references} />}
              </>
            ) : (
              <div className="border-2 border-[#D4D4D4] bg-white p-8 text-center" style={{ boxShadow: "4px 4px 0px 0px #15803D" }}>
                <p className="font-arcade text-2xl text-[#15803D] uppercase mb-2">
                  &gt; COMING SOON
                </p>
                <p className="font-terminal text-sm text-[#0A0A0A]/60">
                  This dispatch is being authored. Check back shortly.
                </p>
              </div>
            )}
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-24">
              {hasBody && <TableOfContents containerSelector='[data-testid="article-prose"]' />}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
